import { useId } from 'react';
import {
  mergePickerItems,
  normalizeClassifiedItems,
  type PickerItem,
} from '@/components/modals/formPickerItems';
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { InputField } from '../../../../../components/formFields/InputField';
import Bar from '../../../../../components/elements/Bar';
import { Plus } from 'lucide-react';
import PickValueWithClassificationModal from '../../../../../components/modals/PickValueWithClassificationModal';
import Label from '@/components/Label';
import { AreaField } from '../../../../../components/formFields/AreaField';
import FormList from '../../../../../components/elements/FormList';

type PropertySetModalProps = {
  visible: boolean;
  title: string;
  formProps?: any;
  onSubmit: (id: any, values: any) => void;
  onClose: () => void;
  classification?: any[];
};

const PropertySetModalForm = ({
  visible,
  title,
  formProps,
  onSubmit,
  onClose,
}: PropertySetModalProps) => {
  const [visibleAddPropertyDialog, setVisibleAddPropertyDialog] = useState(false);

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
    description: Yup.string().nullable().required('Povinné pole'),
  });

  const pickerSession = useId();
  const formRef = useRef<any>(null);

  const handleAddProperty = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'property_definition_set',
      mergePickerItems(formRef.current.values.property_definition_set, items)
    );
  };

  const handleDeleteProperty = (id: any) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.property_definition_set.filter(
      (ele: any) => ele.id !== id
    );

    formRef.current.setFieldValue('property_definition_set', newValues);
  };

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              description: formProps?.description || null,
              property_definition_set: normalizeClassifiedItems(
                formProps?.property_definition_set || [],
                'property_definition'
              ),
            }}
            onSubmit={(values) => {
              onSubmit(formProps?.id, {
                ...values,
                property_definition_set: [
                  ...new Set(values.property_definition_set.map((property: any) => property.id)),
                ],
              });
            }}
          >
            {(props) => {
              const { values } = props;
              return (
                <>
                  <Form>
                    <div className="space-y-1">
                      <Label name="title" title="Název" required />
                      <Field
                        name="title"
                        value={values.title || null}
                        placeholder="Název"
                        component={InputField}
                        data-testid="property-set-modal-title-input"
                      />
                    </div>

                    <div>
                      <Label name="description" title="Popisek" required />
                      <Field
                        name="description"
                        value={values.description || null}
                        placeholder="Popisek"
                        component={AreaField}
                        data-testid="property-set-modal-description-input"
                      />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Vlastnosti"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddPropertyDialog(true)}
                            data-testid="property-set-modal-add-property-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.property_definition_set}
                        onDelete={handleDeleteProperty}
                      />
                    </div>
                  </Form>
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddPropertyDialog}
                    url="left-panel-property-definition"
                    pickerUrl="property-definition/left-panel-picker/"
                    onClose={() => setVisibleAddPropertyDialog(false)}
                    onSubmit={handleAddProperty}
                    alreadySelectedValues={values.property_definition_set}
                  />
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            onClick={() => onClose()}
            data-testid="property-set-modal-close-btn"
          >
            Zavřít
          </Button>
          <Button
            key={2}
            onClick={() => onSubmitButton()}
            data-testid="property-set-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function PropertySetModal(props: React.ComponentProps<typeof PropertySetModalForm>) {
  return props.visible ? (
    <PropertySetModalForm
      key={`${props.formProps?.id ?? 'create'}:${props.formProps?.id_version ?? ''}`}
      {...props}
    />
  ) : null;
}

export default PropertySetModal;
